import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { ArtistScrapeStatus } from 'fym-shared/src/smartlinks/catalog';
import { ArtistPortalOverviewResponse } from 'fym-shared/src/smartlinks/artistPortals';
import { ConnectionInsight } from '../../../redux/api/fymAPI.types';

/** What the tile calls the audience of a service: YouTube has subscribers, Deezer has fans. */
export type AudienceLabelKind = 'followers' | 'subscribers' | 'fans' | 'monthlyListeners';

/** How the tile reads its change: a count difference (a daily reading) or a percent (a portal). */
export type AudienceTileDelta =
  | { mode: 'count'; periodDelta: number | null }
  | { mode: 'percent'; changePct: number | null };

export interface AudienceTileModel {
  key: string;
  integrationType: string;
  labelKind: AudienceLabelKind;
  /** Null when the reading failed: the tile shows "—" and "Not read", never a zero. */
  value: number | null;
  delta: AudienceTileDelta;
  source: 'profile' | 'spotifyForArtists';
}

/** Spotify first, then the order of the design (a1). */
const SERVICE_ORDER: readonly string[] = [
  MusicAPIIntegrationID.Spotify,
  MusicAPIIntegrationID.Youtube,
  MusicAPIIntegrationID.YoutubeMusic,
  MusicAPIIntegrationID.Deezer,
  MusicAPIIntegrationID.AppleMusic,
  MusicAPIIntegrationID.SoundCloud,
];

/** Services that publish no follower count. A failed reading there is not a missing number. */
const NO_COUNT_SERVICES: readonly string[] = [MusicAPIIntegrationID.AppleMusic, MusicAPIIntegrationID.Tidal];

function orderOf(integrationType: string): number {
  const index = SERVICE_ORDER.indexOf(integrationType);
  return index === -1 ? SERVICE_ORDER.length : index;
}

export function audienceLabelKind(integrationType: string): AudienceLabelKind {
  if (integrationType === MusicAPIIntegrationID.Youtube || integrationType === MusicAPIIntegrationID.YoutubeMusic) {
    return 'subscribers';
  }
  if (integrationType === MusicAPIIntegrationID.Deezer) {
    return 'fans';
  }
  return 'followers';
}

/**
 * A follower tile per read service. A service that publishes no count (Apple Music, TIDAL) has no
 * tile. A reading that failed with no value keeps its tile with a null value ("Not read").
 */
export function followerTiles(insights: ConnectionInsight[]): AudienceTileModel[] {
  return insights
    .filter(
      (insight) =>
        insight.followers.latest !== null ||
        (insight.status === ArtistScrapeStatus.failing && !NO_COUNT_SERVICES.includes(insight.integrationType))
    )
    .sort((a, b) => orderOf(a.integrationType) - orderOf(b.integrationType))
    .map((insight) => ({
      key: `followers-${insight.integrationType}`,
      integrationType: insight.integrationType,
      labelKind: audienceLabelKind(insight.integrationType),
      value: insight.followers.latest,
      delta: { mode: 'count', periodDelta: insight.followers.periodDelta ?? null },
      source: 'profile',
    }));
}

/**
 * The monthly listeners tile (owner decision 2): the Spotify for Artists value when that portal
 * reads, else the value the profile reading scraped. `undefined` when neither has one.
 */
export function monthlyListenersTile(
  insights: ConnectionInsight[],
  overview: ArtistPortalOverviewResponse | undefined
): AudienceTileModel | undefined {
  const portal = overview?.services.find((service) => service.integrationType === MusicAPIIntegrationID.Spotify);
  const portalTotal = portal?.totals.monthlyListeners;
  if (portalTotal !== undefined && portalTotal.current !== null) {
    return {
      key: 'monthlyListeners',
      integrationType: MusicAPIIntegrationID.Spotify,
      labelKind: 'monthlyListeners',
      value: portalTotal.current,
      delta: { mode: 'percent', changePct: portalTotal.changePct },
      source: 'spotifyForArtists',
    };
  }
  const scraped = insights.find(
    (insight) => insight.integrationType === MusicAPIIntegrationID.Spotify
  )?.monthlyListeners;
  if (scraped === undefined || scraped === null || scraped.latest === null) {
    return undefined;
  }
  return {
    key: 'monthlyListeners',
    integrationType: MusicAPIIntegrationID.Spotify,
    labelKind: 'monthlyListeners',
    value: scraped.latest,
    delta: { mode: 'count', periodDelta: scraped.periodDelta },
    source: 'profile',
  };
}

/** The Audience row: the Spotify followers, the monthly listeners, then the other services. */
export function audienceTiles(
  insights: ConnectionInsight[],
  overview: ArtistPortalOverviewResponse | undefined
): AudienceTileModel[] {
  const followers = followerTiles(insights);
  const listeners = monthlyListenersTile(insights, overview);
  if (listeners === undefined) {
    return followers;
  }
  const spotifyFirst = followers[0]?.integrationType === MusicAPIIntegrationID.Spotify ? 1 : 0;
  return [...followers.slice(0, spotifyFirst), listeners, ...followers.slice(spotifyFirst)];
}

/** The kind of an Apple Music for Artists number on the Streams row. */
export type StreamsExtraKind = 'shazams' | 'radioSpins';

export interface StreamsExtraTileModel {
  kind: StreamsExtraKind;
  value: number;
}

/**
 * The Shazams and radio spins tiles of the Streams row (Apple Music for Artists). A tile shows only
 * when its value is a number: 0 shows, null does not. No `extras` (an older server or the demo
 * artist) gives no tile.
 */
export function streamsExtraTiles(overview: ArtistPortalOverviewResponse | undefined): StreamsExtraTileModel[] {
  const extras = overview?.extras;
  if (extras === undefined) {
    return [];
  }
  const tiles: StreamsExtraTileModel[] = [];
  if (typeof extras.shazams === 'number') {
    tiles.push({ kind: 'shazams', value: extras.shazams });
  }
  if (typeof extras.radioSpins === 'number') {
    tiles.push({ kind: 'radioSpins', value: extras.radioSpins });
  }
  return tiles;
}
